/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CovenantComplianceCount } from '../models/CovenantComplianceCount';
import type { DetailedCovenantSummary } from '../models/DetailedCovenantSummary';
import type { DetailedTimeSeriesModel } from '../models/DetailedTimeSeriesModel';
import type { FieldRead } from '../models/FieldRead';
import type { ListCovenantSummariesForNamespacePayload } from '../models/ListCovenantSummariesForNamespacePayload';
import type { PaginatedResult } from '../models/PaginatedResult';
import type { PeriodTypeEnum } from '../models/PeriodTypeEnum';
import type { TimeSeriesModelCreatePayload } from '../models/TimeSeriesModelCreatePayload';
import type { TimeSeriesModelRead } from '../models/TimeSeriesModelRead';
import type { TimeSeriesModelUpdatePayload } from '../models/TimeSeriesModelUpdatePayload';
import type { TimeSeriesModelWithColumns } from '../models/TimeSeriesModelWithColumns';
import type { TrackedTimeSeriesModelRead } from '../models/TrackedTimeSeriesModelRead';
import type { UpdateTimeSeriesModelColumnsPayload } from '../models/UpdateTimeSeriesModelColumnsPayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class TsmService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Detailed Time Series Models For Namespace Id
     * @param namespaceId
     * @param periodType
     * @returns DetailedTimeSeriesModel Successful Response
     * @throws ApiError
     */
    public listDetailedTimeSeriesModelsForNamespaceId(
        namespaceId: string,
        periodType?: (PeriodTypeEnum | null),
    ): CancelablePromise<Array<DetailedTimeSeriesModel>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/list-detailed-time-series-models-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            query: {
                'period_type': periodType,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Detailed Time Series Model
     * @param tsmId
     * @param periodType
     * @param globalAsOfDate
     * @returns DetailedTimeSeriesModel Successful Response
     * @throws ApiError
     */
    public getDetailedTimeSeriesModel(
        tsmId: string,
        periodType?: (PeriodTypeEnum | null),
        globalAsOfDate?: (string | null),
    ): CancelablePromise<DetailedTimeSeriesModel> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/get-detailed-time-series-model/{tsm_id}',
            path: {
                'tsm_id': tsmId,
            },
            query: {
                'period_type': periodType,
                'global_as_of_date': globalAsOfDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Time Series Models For Namespace Id
     * @param namespaceId
     * @returns TimeSeriesModelWithColumns Successful Response
     * @throws ApiError
     */
    public listTimeSeriesModelsForNamespaceId(
        namespaceId: string,
    ): CancelablePromise<Array<TimeSeriesModelWithColumns>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/list-time-series-models-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Time Series Models For Entity
     * @param entityId
     * @returns TimeSeriesModelRead Successful Response
     * @throws ApiError
     */
    public listTimeSeriesModelsForEntity(
        entityId: string,
    ): CancelablePromise<Array<TimeSeriesModelRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/list-time-series-models-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Compliance Counts For Entity
     * @param entityId
     * @param globalAsOfDate
     * @returns CovenantComplianceCount Successful Response
     * @throws ApiError
     */
    public getComplianceCountsForEntity(
        entityId: string,
        globalAsOfDate?: (string | null),
    ): CancelablePromise<Record<string, CovenantComplianceCount>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/get-compliance-counts-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            query: {
                'global_as_of_date': globalAsOfDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tracked Time Series Models
     * @param tsmId
     * @returns TrackedTimeSeriesModelRead Successful Response
     * @throws ApiError
     */
    public listTrackedTimeSeriesModels(
        tsmId: string,
    ): CancelablePromise<Array<TrackedTimeSeriesModelRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/list-tracked-time-series-models/{tsm_id}',
            path: {
                'tsm_id': tsmId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Entities Tracking Time Series Model
     * @param tsmId
     * @returns string Successful Response
     * @throws ApiError
     */
    public listEntitiesTrackingTimeSeriesModel(
        tsmId: string,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/list-entities-tracking-time-series-model',
            query: {
                'tsm_id': tsmId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Covenant Summaries For Namespace
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns DetailedCovenantSummary Successful Response
     * @throws ApiError
     */
    public listCovenantSummariesForNamespace(
        clusterId: string,
        namespaceId: string,
        requestBody: ListCovenantSummariesForNamespacePayload,
    ): CancelablePromise<Record<string, Array<DetailedCovenantSummary>>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/tsm/list-covenant-summaries-for-namespace',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Time Series Model For Entity
     * @param entityId
     * @param tsmId
     * @param periodType
     * @param startDate
     * @param endDate
     * @param globalAsOfDate
     * @returns DetailedTimeSeriesModel Successful Response
     * @throws ApiError
     */
    public getTimeSeriesModelForEntity(
        entityId: string,
        tsmId: string,
        periodType: PeriodTypeEnum,
        startDate: string,
        endDate: string,
        globalAsOfDate?: (string | null),
    ): CancelablePromise<DetailedTimeSeriesModel> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/tsm/get-time-series-model-for-entity/{entity_id}/{tsm_id}/',
            path: {
                'entity_id': entityId,
                'tsm_id': tsmId,
            },
            query: {
                'period_type': periodType,
                'start_date': startDate,
                'end_date': endDate,
                'global_as_of_date': globalAsOfDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Time Series Model
     * @param namespaceId
     * @param requestBody
     * @returns DetailedTimeSeriesModel Successful Response
     * @throws ApiError
     */
    public createTimeSeriesModel(
        namespaceId: string,
        requestBody: TimeSeriesModelCreatePayload,
    ): CancelablePromise<DetailedTimeSeriesModel> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/tsm/create-time-series-model/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Time Series Model Columns
     * @param clusterId
     * @param namespaceId
     * @param tsmId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateTimeSeriesModelColumns(
        clusterId: string,
        namespaceId: string,
        tsmId: string,
        requestBody: UpdateTimeSeriesModelColumnsPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/tsm/update-time-series-model-columns/',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
                'tsm_id': tsmId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Time Series Model
     * @param tsmId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteTimeSeriesModel(
        tsmId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/tsm/delete-time-series-model/{tsm_id}',
            path: {
                'tsm_id': tsmId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Tsm For Entity
     * @param entityId
     * @param timeSeriesModelId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForTsmForEntity(
        entityId: string,
        timeSeriesModelId?: (string | null),
        cursor?: (string | null),
        pageSize: number = 25,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/list-audit-logs-for-tsm-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            query: {
                'time_series_model_id': timeSeriesModelId,
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Tsm For Namespace
     * @param namespaceId
     * @param timeSeriesModelId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForTsmForNamespace(
        namespaceId: string,
        timeSeriesModelId?: (string | null),
        cursor?: (string | null),
        pageSize: number = 25,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/list-audit-logs-for-tsm-for-namespace/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            query: {
                'time_series_model_id': timeSeriesModelId,
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Tsmc For Property
     * @param propertyId
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public getTsmcForProperty(
        propertyId: string,
    ): CancelablePromise<FieldRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/tsm/get-tsmc-for-property/{property_id}',
            path: {
                'property_id': propertyId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add Tracked Time Series Models
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public addTrackedTimeSeriesModels(
        entityId: string,
        requestBody: Array<string>,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/tsm/add-tracked-time-series-models',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Remove Tracked Time Series Model
     * @param entityId
     * @param tsmId
     * @returns any Successful Response
     * @throws ApiError
     */
    public removeTrackedTimeSeriesModel(
        entityId: string,
        tsmId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/tsm/remove-tracked-time-series-model/{tsm_id}',
            path: {
                'entity_id': entityId,
                'tsm_id': tsmId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tracked Time Series Models For Cluster
     * @param clusterId
     * @returns TrackedTimeSeriesModelRead Successful Response
     * @throws ApiError
     */
    public listTrackedTimeSeriesModelsForCluster(
        clusterId: string,
    ): CancelablePromise<Array<TrackedTimeSeriesModelRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/tsm/list-tracked-time-series-models-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Time Series Model
     * @param clusterId
     * @param namespaceId
     * @param tsmId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateTimeSeriesModel(
        clusterId: string,
        namespaceId: string,
        tsmId: string,
        requestBody: TimeSeriesModelUpdatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/tsm/update-time-series-model/{namespace_id}/{tsm_id}',
            path: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
                'tsm_id': tsmId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
